/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useState, useMemo, useReducer, useRef } from 'react';
import _ from 'lodash';
import { saveAs } from 'file-saver';
import JsonQuery from 'json-query';
import type { CellObject, ColInfo, Range, RowInfo, WorkBook, WorkSheet } from 'xlsx-js-style';
import { useLocation } from 'react-router';
import {
    hooks as commonHooks,
    types as commonTypes,
    analytics,
    utils as commonUtils,
    baseComponents,
} from '@sitebuilder/common';
import {
    getLayoutSchemaProperties,
    getStyleSchemaProperties,
    getTextSchemaProperties,
} from '../utils';
// import MultiSelectModal from '../../BaseComponents/MultiSelectModal';
// import { hooks as commonHooks } from '@sitebuilder/common';
// import Placeholder from '../../Placeholder';
// import Filters, { Filter, FiltersUI } from '../../BaseComponents/Filters';
// import { filtersStyles } from '../../BaseComponents/Filters/Filters.styles';
import Table from './Table';
import { sampleData } from './sampleData';
import Pagination from './Pagination';
import { customizedFundListStyles } from './CustomizedFundList.styles';
import Tabs from './Tabs';
import DownloadBooklet from './DownloadBooklet';

interface DynamicEvent {
    eventName: string;
    customEventName?: string;
    analyticItems: AnalyticsItem[];
}

interface AnalyticsItem {
    name: string;
    value: string;
}

export interface DownloadBookletOptions {
    downloadBooklet?: boolean;
    downloadBookletButtonText?: string;
    showDownloadBookletIcon?: boolean;
    downloadBookletIconType?: 'FontAwesome Icon' | 'Image';
    downloadBookletIconPosition?: 'Left' | 'Right';
    downloadBookletIconName?: string;
    downloadBookletImage?: string;
    downloadBookletMessage?: string;
    downloadBookletMessageIcon?: boolean;
    downloadBookletMessageIconType?: 'FontAwesome Icon' | 'Image';
    downloadBookletMessageIconName?: string;
    downloadBookletMessageIconPosition?: 'Left' | 'Right';
    downloadBookletMessageImage?: string;
    downloadBookletErrorMessage?: string;
    downloadBookletErrorMessageIcon?: boolean;
    downloadBookletErrorMessageIconType?: 'FontAwesome Icon' | 'Image';
    downloadBookletErrorMessageIconPosition?: 'Left' | 'Right';
    downloadBookletErrorMessageIconName?: string;
    downloadBookletErrorMessageImage?: string;
    downloadLinkText?: string;
    downloadBookletText?: string;
    bookletName?: string;
}

export interface ColumnOption {
    id?: string;
    label: string;
    labelDescription?: string;
    type: string;
    columnGrouping?: string;
    value?: string;
    placeholderId?: string;
    isSortable?: boolean;
    iconType?: '' | 'FontAwesome icon' | 'Image';
    iconPosition?: 'Right' | 'Left';
    iconSort?: string;
    iconUp?: string;
    iconDown?: string;
    imageSort?: string;
    imageUp?: string;
    imageDown?: string;
    sortId?: string;
    sortAnalyticLabel?: string;
    watchlistType?: string;
    selectedWatchlistIconName?: string;
    watchlistIconName?: string;
    excludeWatchListOpt?:
        | ''
        | 'Individual Fund Ids'
        | 'Data key for Fund Ids'
        | 'Data key for condition';
    excludeWatchListFundIds?: string;
    excludeWatchListFundIdsDataKey?: string;
    excludeWatchListConditionDataKey?: string;
    isIncludeInDownloadFile?: boolean;
    downloadDataValue?: string;
    downloadHeaderValue?: string;
    selectionListId?: string;
    selectionUsedFor?: 'compare' | 'downloadBooklet';
    selectionListStorageName?: string;
    addToSelectionlistText?: string;
    removeFromSelectionlistText?: string;
    changeToTab?: string;
    excludeCompareOpt?:
        | ''
        | 'Individual Fund Ids'
        | 'Data key for Fund Ids'
        | 'Data key for condition';
    excludeCompareFundIds?: string;
    excludeCompareFundIdsDataKey?: string;
    excludeCompareConditionDataKey?: string;
    maxSelectionCount?: number;
    minSelectionCount?: number;
    ActiveButtonText?: string;
    disableVisibilityToggle?: boolean;
}

export interface Tab {
    id?: string;
    name?: string;
    downloadTabName?: string;
    tabColumns?: string;
    haveDiffDownloadColumns?: boolean;
    downloadColumns?: string;
    isDefaultSelected?: boolean;
    footerText?: string;
    headerText?: string;
    hideColumnVisibilityToggle?: boolean;
    useTransformFunction?: boolean;
    functionToTransform?: string;
    hasTabFilter?: boolean;
    filters?: baseComponents.filters.Filter[];
    filtersUI?: baseComponents.filters.FiltersUI;
    tabParamValue?: string;
}

export interface CustomizedFundListOptions extends DownloadBookletOptions {
    columns: ColumnOption[];
    rows: string;
    enableGrouping: boolean;
    groupingPropertyId: string;
    showGroupingCount: boolean;
    isCollapseRowGroup: boolean;
    showRowGroupIconName: string;
    hideRowGroupIconName: string;
    isAddPlaceholderInLeftPanelOfTableCtrls: boolean;
    isAddPlaceholderInRightPanelOfTableCtrls: boolean;
    isAddPlaceholderInBottomPanelOfTableCtrls?: boolean;
    typographyClass?: string;
    enableWatchlist: boolean;
    watchlistStorageName: string;
    watchlistDataId: string;
    watchlistButtonText: string;
    addToWatchlistText: string;
    removeFromWatchlistText: string;
    defaultPageSize?: number;
    showPageSizePicker: boolean;
    enablePagination: boolean;
    configurePageSizes: string;
    showCurrentPageText: boolean;
    pagingType: string;
    pagePickerPosition: string;
    pageSizePickerPosition: string;
    pagePickerDropdownImage: string;
    currentPageTextPosition: string;
    pagerButtonStyle: string;
    currentPageText: string;
    previousBtnText: string;
    nextBtnText: string;
    firstBtnText: string;
    lastBtnText: string;
    showAllFundsBtnText: string;
    pageSizePickerText: string;
    filters: baseComponents.filters.Filter[];
    filtersUI: baseComponents.filters.FiltersUI;
    enableDownload: boolean;
    downloadButtonText: string;
    downloadFileName: string;
    downloadFileType: string;
    downloadFileTabName: string;
    fileNameBasedOnTab?: boolean;
    showDownloadAtBottom?: boolean;
    addPlaceholderWhenNoData: boolean;
    tabs?: Tab[];
    tabUrlParam?: string;
    hideTabs?: boolean;
    isTabsSticky?: boolean;
    mobileBreakpoint?: string;
    disableTabStaticData?: boolean;
    disableLocalStorage?: boolean;
    defaultSortId?: string;
    defaultSortingOrder?: string;
    dynamicEvents?: DynamicEvent[];
    columnGroups?: {
        id: string;
        label: string;
    }[];
    enableColumnToggling?: boolean;
    columnTogglingTitle?: string;
    columnTogglingSubtitle?: string;
    columnTogglingSaveText?: string;
    columnTogglingCancelText?: string;
    columnTogglingSelectAllText?: string;
    columnTogglingDeselectAllText?: string;
    moveControlsToTop?: boolean;
}

export interface SelectionListData {
    storageName: string;
    id: string;
    changeToTab?: string;
    colId: string;
}

export interface WidgetProperties
    extends commonTypes.WidgetPropertiesBase,
        CustomizedFundListOptions {}

export interface Widgetprops extends commonTypes.WidgetInstanceProps {}

const CHILDREN_PLACEHOLDER_ID = 'tableCntrls';

const CustomizedFundList = (props: Widgetprops) => {
    const { data } = props;
    const i18n = commonHooks.useLocalization();
    const tracking = analytics.useTracking();
    const location = useLocation();
    const search = location.search;
    const params = new URLSearchParams(search);
    const { Placeholder } = commonHooks.useAppContext();

    const widgetProperties: any =
        commonHooks.useWidgetProperties<WidgetProperties>(props.widgetInstanceId) ||
        props?.properties;
    const { disableTabStaticData: globalDisableTabStaticData } =
        commonHooks.useWidgetProperties<WidgetProperties>('root') || {};
    const {
        styles,
        columns,
        rows,
        enablePagination,
        defaultPageSize,
        showPageSizePicker,
        filters,
        filtersUI,
        enableGrouping,
        groupingPropertyId,
        groupingJsonPath,
        useAnalytics,
        useLowerCase,
        dynamicEvents,
        ...rest
    } = widgetProperties || props;

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(defaultPageSize || 10);
    const [showAllRows, setShowAllRows] = useState(false);

    // watchlist
    const [watchlist, setWatchlist] = useState<any>([]);
    const [filterByWatchList, setFilterByWatchlist] = useState<boolean>(false);

    // compare
    const [filterBySelection, setFilterBySelection] = useState<SelectionListData[]>([]);
    const [selectionLists, setSelecionLists] = useState<Record<string, string[]>>({});

    // TODO: remove sample data after actual data available
    const _data = { ...data, sampleData };
    const theme = props?.data?.themeSelected || props?.theme;
    const widgetInstance = commonHooks.useWidgetInstance(props.widgetInstanceId);

    const rowsData = JsonQuery(rows as string, { data: _data }).value || [];
    const [transformedTabData, setTransformedTabData] = useState<any>({ rowsData });
    const [filteredData, setFilteredData] = useState(rowsData);
    const [filteredRowsData, setFilteredRowsData] = useState(filteredData);
    const [rowsDataLength, setRowsDataLength] = useState<number>(rowsData.length);
    const [sorting, setSorting] = useState(rest.defaultSortId ? 'ascending' : '');
    const [sortId, setSortId] = useState(rest.defaultSortId);
    const [textFilters, setTextFilters] = useState<any>([]);
    const [dateFilter, setDateFilter] = useState<any>(null);
    const [multiSelectFilters, setMultiSelectFilters] = useState<any>([]);
    // set tab filters if hasTabFilter is true for tab
    // state of array of data and filters for tab
    // in format of [{[tabName]: {data: [], textFilters: [], dateFilter: null, dropdownFilters: []}}]
    const [tabFilters, setTabFilters] = useState<any>(null);

    const defaultSelectedTab: string = rest?.tabUrlParam
        ? params.get(rest?.tabUrlParam)
            ? rest?.tabs?.find(
                  (tab: Tab) =>
                      i18n.format(tab?.tabParamValue || '', data) === params.get(rest?.tabUrlParam)
              )?.id || params.get(rest?.tabUrlParam)
            : rest?.tabs?.find((tab: Tab) => tab?.isDefaultSelected)?.id || rest?.tabs?.[0]?.id
        : rest?.tabs?.find((tab: Tab) => tab?.isDefaultSelected)?.id || rest?.tabs?.[0]?.id;

    const prevColumnsRef = useRef<ColumnOption[] | []>(columns);

    const [selectedTab, setSelectedTab] = useState<string>(defaultSelectedTab);
    const [tabCols, setTabCols] = useState<ColumnOption[] | []>([]);
    const [downloadTabCols, setDownloadTabCols] = useState<ColumnOption[] | []>([]);
    const [selectedTabName, setSelectedTabName] = useState<string>('');
    const [selectedDownloadTabName, setSelectedDownloadTabName] = useState<string>('');
    const [shownColumns, setShownColumns] = useState<ColumnOption[]>(columns);
    const [singleDateFilter, setSingleDateFilter] = useState<{
        date: string;
        filterBy: string;
    } | null>(null);

    const staticDataTabIndex = globalDisableTabStaticData || rest.disableTabStaticData ? -1 : 0;
    const firstPageIndex = (currentPage - 1) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;
    const pageFrom = (currentPage - 1) * pageSize + 1;
    const pageTo = lastPageIndex >= filteredData.length ? filteredData.length : lastPageIndex;

    const getGroupedData = (data: any, id: string) => {
        const groupsCount: any = {};
        return {
            data: data?.reduce((acc: any, model: any) => {
                const { value }: any = JsonQuery(id, {
                    data: model,
                });

                if (acc[value]) {
                    acc[value] = [...acc[value], model];
                    groupsCount[value]++;
                } else {
                    acc[value] = [model];
                    groupsCount[value] = 1;
                }
                return acc;
            }, {}),
            groupsCount,
        };
    };

    const onClickCompare = (
        storageName: string,
        id: string,
        colId: string,
        changeToTab?: string
    ) => {
        const index = filterBySelection?.findIndex((d) => d.storageName === storageName);
        let tmp = filterBySelection || [];
        if (typeof index === 'number' && index >= 0) {
            tmp = tmp.filter((_, i: number) => i !== index);
        } else if (typeof index === 'number') {
            tmp.push({ storageName, id, colId, changeToTab });
        }

        if (changeToTab && selectedTab !== changeToTab) {
            setSelectedTab(changeToTab);
        }
        setFilterBySelection([...tmp]);
        setCurrentPage(1);

        tracking({
            event: index ? 'Compare Button' : 'Active Compare Button',
            data: data,
            dynamicEvents: dynamicEvents,
            useAnalytics: useAnalytics,
            useLowerCase: useLowerCase,
        });
    };

    const _tab = selectedTab
        ? rest?.tabs?.find((tab: Tab) => tab?.id === selectedTab)
        : rest?.tabs?.[0];

    useEffect(() => {
        if (_tab?.useTransformFunction) {
            const transformFunctionName = `${props.widgetInstanceId}-${_tab.id}-transform`;

            try {
                // eslint-disable-next-line no-new-func
                const transformedData =
                    _tab?.useTransformFunction &&
                    commonUtils.transformDataWithGlobals(
                        transformFunctionName,
                        _tab?.functionToTransform,
                        {
                            ...data,
                            rowsData,
                        }
                    );

                if (transformedData) {
                    setTransformedTabData({ ...transformedTabData, ...transformedData });
                    if (transformedData?.rowsData) {
                        setFilteredData(transformedData?.rowsData);
                    }
                }
            } catch (e) {
                console.log(e);
            }
        } else {
            setTransformedTabData({ ...transformedTabData, rowsData });
            setFilteredData(rowsData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_tab?.useTransformFunction, _tab?.functionToTransform, data]);

    useEffect(() => {
        if (rest.disableLocalStorage) {
            localStorage.removeItem('Comparestoragees');
        }
    }, [rest.disableLocalStorage]);

    useEffect(() => {
        if (rest?.tabs && rest?.tabs?.length) {
            const _tabColumns = _tab?.tabColumns?.split(',');
            const _tabCols = shownColumns?.filter((col: ColumnOption) =>
                _tabColumns?.includes(col?.id)
            );

            // set download columns if haveDiffDownloadColumns is true
            if (_tab.haveDiffDownloadColumns) {
                const _downloadTabColumns = _tab?.downloadColumns?.split(',');
                const _downloadTabCols = shownColumns?.filter((col: ColumnOption) =>
                    _downloadTabColumns?.includes(col?.id)
                );
                setDownloadTabCols(_downloadTabCols);
            }
            setSelectedTabName(_tab.name);
            setSelectedDownloadTabName(_tab.downloadTabName || '');
            setTabCols(_tabCols);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rest?.tabs, selectedTab, shownColumns]);

    useEffect(() => {
        if (!_.isEqual(prevColumnsRef.current, columns)) {
            const colsObject: any = columns?.reduce((acc: any, col: any) => {
                acc[col.id] = col;
                return acc;
            }, {});
            setShownColumns(
                shownColumns?.map((col: any) => {
                    if (Object.prototype.hasOwnProperty.call(colsObject, col.id)) {
                        return {
                            ...col,
                            ...colsObject[col.id],
                        };
                    } else {
                        return col;
                    }
                })
            );
            prevColumnsRef.current = columns;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns]);

    useEffect(() => {
        if (filterByWatchList) {
            setCurrentPage(1);
            // tracks the watchlist filter when applied
            tracking({
                event: 'Watchlist Filter',
                data: data,
                dynamicEvents: dynamicEvents,
                useAnalytics: useAnalytics,
                useLowerCase: useLowerCase,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterByWatchList]);

    useEffect(() => {
        if (filterByWatchList && watchlist.length === 0) {
            setFilterByWatchlist(false);
        }

        if (filterBySelection.length > 0 && selectionLists.Comparestoragees.length === 0) {
            setFilterBySelection([]);
        }
    }, [watchlist, filterByWatchList, selectionLists, filterBySelection]);

    const onChangeSorting = (sorting: string, sortId: string, sortAnalyticLabel: string) => {
        setSorting(sorting);
        setSortId(sortId);

        if (sortAnalyticLabel) {
            tracking({
                event: 'Sort Filter',
                data,
                dynamicEvents,
                useAnalytics,
                filterAnalyticLabel: sortAnalyticLabel,
                useLowerCase: useLowerCase,
            });
        }
    };

    const updateFilters = (currentFilters: any, value: string | string[], filterBy?: string) => {
        if (!filterBy) {
            // If filterBy is not provided, just append the new filter
            return [...currentFilters, { value, filterBy }];
        }

        const filterByParts = filterBy.split(' ');
        const uniqueFilterBySet = new Set(filterByParts);

        // Filter out existing filters that have matching filterBy values
        const uniqueTextFilters = currentFilters?.filter(
            (item: any) => !uniqueFilterBySet.has(item.filterBy)
        );

        // Create new filter objects for each filterBy part
        const newFilters = filterByParts?.map((filter) => ({
            value,
            filterBy: filter,
        }));

        return [...uniqueTextFilters, ...newFilters];
    };

    const onChangeTextFilter = (
        value: string,
        filterBy?: string,
        analyticsLabel?: string,
        isTabFilter?: boolean
    ) => {
        if (isTabFilter) {
            // Update tab-specific filters
            setTabFilters({
                ...tabFilters,
                [selectedTab]: {
                    ...tabFilters?.[selectedTab],
                    textFilters: updateFilters(
                        tabFilters?.[selectedTab]?.textFilters || [],
                        value,
                        filterBy
                    ),
                },
            });
        } else {
            // Update global filters
            setTextFilters(updateFilters(textFilters, value, filterBy));
        }

        tracking({
            event: 'Text Filter',
            data,
            dynamicEvents,
            useAnalytics,
            filterAnalyticLabel: analyticsLabel,
            selectedTextFilter: value,
            useLowerCase: useLowerCase,
        });
    };

    const onClickTextFilter = (analyticsLabel?: string) => {
        tracking({
            event: 'Click Text Filter',
            data,
            dynamicEvents,
            useAnalytics,
            filterAnalyticLabel: analyticsLabel,
            useLowerCase: useLowerCase,
        });
    };

    const [dropdownFilters, setDropdownFilters] = useReducer((prevState: any, newState: any) => {
        if (newState === null) {
            // Reset state if new state is null
            return [];
        } else if (newState?.filterBy === null) {
            // Return previous state if new state is empty
            return prevState;
        } else if (newState?.type === 'save') {
            if (newState?.isTabFilter) {
                // Handle tab-specific dropdown filters
                const updatedTabFilters = {
                    ...tabFilters,
                    [selectedTab]: {
                        ...tabFilters?.[selectedTab],
                        dropdownFilters: Object.values(
                            [
                                ...(tabFilters?.[selectedTab]?.dropdownFilters || []),
                                newState,
                            ].reduce((acc, curr) => {
                                acc[curr?.filterBy] = curr;
                                return acc;
                            }, [])
                        ),
                    },
                };

                setTabFilters(updatedTabFilters);
                return prevState; // Return previous state for global filters
            } else {
                // Handle global dropdown filters
                const _dropdownFilters = Object.values(
                    [...prevState, newState].reduce((acc, curr) => {
                        acc[curr?.filterBy] = curr;
                        return acc;
                    }, [])
                );

                return _dropdownFilters;
            }
        } else if (newState?.type === 'remove') {
            if (newState?.isTabFilter) {
                // Handle tab-specific removal
                const updatedTabFilters = {
                    ...tabFilters,
                    [selectedTab]: {
                        ...tabFilters?.[selectedTab],
                        dropdownFilters: (tabFilters?.[selectedTab]?.dropdownFilters || []).map(
                            (state: any) => {
                                if (state?.filterBy === newState?.filterBy) {
                                    return {
                                        ...state,
                                        selectedOpts: state?.selectedOpts?.filter(
                                            (opt: string) => opt !== newState?.opt
                                        ),
                                    };
                                }
                                return state;
                            }
                        ),
                    },
                };

                setTabFilters(updatedTabFilters);
                return prevState; // Return previous state for global filters
            } else {
                // Handle global dropdown filters removal
                const _dropdownFilters = prevState?.map((state: any) => {
                    if (state?.filterBy === newState?.filterBy) {
                        return {
                            ...state,
                            selectedOpts: state?.selectedOpts?.filter(
                                (opt: string) => opt !== newState?.opt
                            ),
                        };
                    }
                    return state;
                });

                return _dropdownFilters;
            }
        }
    }, []);

    const onChangeDropdownOpt = (
        selectedOpts: string[],
        filterBy?: string,
        label?: string,
        defaultValue?: string,
        analyticsLabel?: string,
        _filterByKey?: string,
        selectAllLabel?: string,
        selectCurrentOption?: string,
        isTabFilter?: boolean
    ) => {
        if (selectAllLabel && selectedOpts.includes(selectAllLabel)) {
            selectedOpts = [];
        }

        const filterValue = selectCurrentOption;

        setDropdownFilters({
            type: 'save',
            selectedOpts,
            filterBy,
            label,
            defaultValue,
            isTabFilter,
        });

        if (selectedOpts && selectedOpts.length > 0) {
            tracking({
                event: 'Dropdown Filter',
                data: data,
                dynamicEvents: dynamicEvents,
                useAnalytics: useAnalytics,
                filterAnalyticLabel: analyticsLabel,
                filterValue: filterValue,
                useLowerCase: useLowerCase,
            });
        }
    };

    const onChangeMultiSelect = (
        selectedOpts: string[],
        filterBy?: string,
        isTabFilter?: boolean
    ) => {
        if (isTabFilter) {
            // Update tab-specific filters
            setTabFilters({
                ...tabFilters,
                [selectedTab]: {
                    ...tabFilters?.[selectedTab],
                    multiSelectFilters: [
                        // Remove any existing filter with the same filterBy
                        ...(tabFilters?.[selectedTab]?.multiSelectFilters || []).filter(
                            (f: any) => f.filterBy !== filterBy
                        ),
                        { filterBy, selectedOpts },
                    ],
                },
            });
        } else {
            // Update global filters
            setMultiSelectFilters([
                // Remove any existing filter with the same filterBy
                ...multiSelectFilters.filter((f: any) => f.filterBy !== filterBy),
                { filterBy, selectedOpts },
            ]);
        }
    };

    const onCancelFilter = (
        filter: { opt: string; filterBy: string },
        filterType: 'dropdown' | 'multiSelect',
        isTabFilter?: boolean
    ) => {
        if (filterType === 'dropdown') {
            const removeFilter = { type: 'remove', opt: filter?.opt, filterBy: filter?.filterBy };

            if (isTabFilter) {
                setTabFilters({
                    ...tabFilters,
                    [selectedTab]: {
                        ...tabFilters?.[selectedTab],
                        dropdownFilters: tabFilters?.[selectedTab]?.dropdownFilters?.map(
                            (state: any) =>
                                state.filterBy === removeFilter.filterBy
                                    ? {
                                          ...state,
                                          selectedOpts: state.selectedOpts.filter(
                                              (opt: any) => opt !== filter.opt
                                          ),
                                      }
                                    : state
                        ),
                    },
                });
            } else if (filter?.opt && filter?.filterBy) {
                setDropdownFilters(removeFilter);
            }
        } else if (filterType === 'multiSelect') {
            if (isTabFilter) {
                // Update tab-specific multi-select filters
                setTabFilters({
                    ...tabFilters,
                    [selectedTab]: {
                        ...tabFilters?.[selectedTab],
                        multiSelectFilters: tabFilters?.[selectedTab]?.multiSelectFilters?.map(
                            (state: any) =>
                                state.filterBy === filter.filterBy
                                    ? {
                                          ...state,
                                          selectedOpts: state.selectedOpts.filter(
                                              (opt: any) => opt !== filter.opt
                                          ),
                                      }
                                    : state
                        ),
                    },
                });
            } else {
                // Update global multi-select filters
                setMultiSelectFilters(
                    multiSelectFilters?.map((state: any) =>
                        state.filterBy === filter.filterBy
                            ? {
                                  ...state,
                                  selectedOpts: state?.selectedOpts?.filter(
                                      (opt: any) => opt !== filter.opt
                                  ),
                              }
                            : state
                    )
                );
            }
        }
    };

    const onChangeDateRange = (
        minDate: any,
        maxDate: any,
        filterBy?: string,
        analyticsLabel?: string,
        _startDateFilterByKey?: string,
        _endDateFilterByKey?: string,
        isTabFilter?: boolean
    ) => {
        const dateFilter = { minDate, maxDate, filterBy };

        if (isTabFilter) {
            // Update tab-specific date filter
            setTabFilters({
                ...tabFilters,
                [selectedTab]: {
                    ...tabFilters?.[selectedTab],
                    dateFilter: dateFilter,
                },
            });
        } else {
            // Update global date filter
            setDateFilter(dateFilter);
        }

        tracking({
            event: 'Date Filter',
            data: data,
            dynamicEvents: dynamicEvents,
            useAnalytics: useAnalytics,
            filterAnalyticLabel: analyticsLabel,
            useLowerCase: useLowerCase,
        });
    };

    useEffect(() => {
        if (!showPageSizePicker) {
            setPageSize(defaultPageSize || 10);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPageSizePicker, defaultPageSize]);

    const sortItemsWithoutGrouping = (sortId: string, sorting: string) => {
        return (a: any, b: any) => {
            const valueA = a[sortId];
            const valueB = b[sortId];

            // Convert "—" and undefined to 0 for numeric comparison
            const numericValueA =
                valueA === '—' || valueA === null || valueA === undefined ? 0 : valueA;
            const numericValueB =
                valueB === '—' || valueB === null || valueB === undefined ? 0 : valueB;

            if (typeof numericValueA === 'number' && typeof numericValueB === 'number') {
                // Compare numeric values
                return sorting === 'ascending'
                    ? numericValueA - numericValueB
                    : numericValueB - numericValueA;
            } else if (typeof numericValueA === 'number' && valueB === '—') {
                // Place "—" values at the end
                return sorting === 'ascending' ? -1 : 1;
            } else if (valueA === '—' && typeof numericValueB === 'number') {
                // Place "—" values at the end
                return sorting === 'ascending' ? 1 : -1;
            } else {
                // Compare string values
                return sorting === 'ascending'
                    ? String(valueA || '—').localeCompare(String(valueB || '—'), undefined, {
                          sensitivity: 'base',
                      })
                    : String(valueB || '—').localeCompare(String(valueA || '—'), undefined, {
                          sensitivity: 'base',
                      });
            }
        };
    };

    const onTriggerTracking = (
        event: string,
        currentTab?: string,
        fundName?: string,
        filterAnalyticLabel?: string,
        customValue?: string
    ) => {
        tracking({
            event: event,
            data: data,
            dynamicEvents: dynamicEvents,
            useAnalytics: useAnalytics,
            selectedTabValue: currentTab || selectedTabName,
            fundName: fundName || '',
            filterAnalyticLabel: filterAnalyticLabel || '',
            analyticsCustomValue: customValue || '',
            useLowerCase: useLowerCase,
        });
    };

    const onDownloadClick = () => {
        const fileType = rest.downloadFileType;
        let _rowsData = filteredData;
        if (filterByWatchList && watchlist.length) {
            _rowsData = filteredData.filter((d: any) =>
                watchlist.includes(d[rest.watchlistDataId || 'id'])
            );
        }
        if (filterBySelection.length) {
            const selectedCompare = Object.values(selectionLists).flat() || [];
            if (selectedCompare.length) {
                _rowsData = filteredData.filter((d: any) =>
                    selectedCompare.includes(d[filterBySelection[0].id || 'id'])
                );
            }
        }

        let flattenedRowsData = _.isArray(_rowsData) ? _rowsData.flat() : [];

        // remove grouping if any (WE ARE ALWAYS EXPORTING A FLAT LIST)
        if (enableGrouping && groupingPropertyId && flattenedRowsData) {
            const tmp: any = [];
            const _data = getGroupedData(flattenedRowsData, groupingPropertyId)?.data;
            Object.keys(_data).forEach((d) => {
                tmp.push(_data[d]);
            });
            flattenedRowsData = tmp.flat();
        }
        flattenedRowsData.sort(sortItemsWithoutGrouping(sortId, sorting));

        // Initialize workbook
        import('xlsx-js-style').then(({ utils, write }) => {
            const wb: WorkBook = utils?.book_new();

            const rowsData: any = []; // worksheet data
            const wsCols: ColInfo[] = []; // worksheet column options
            const wsRows: RowInfo[] = []; // worksheet row options
            const wsMerges: Range[] = []; // worksheet merges options
            flattenedRowsData?.forEach((r: any) => {
                const row: any = {};
                // get selected tab
                const _tab: Tab = selectedTab
                    ? rest?.tabs?.find((tab: Tab) => tab?.id === selectedTab)
                    : rest?.tabs?.[0];

                // select columns based on tab selection
                const cols = _tab?.haveDiffDownloadColumns
                    ? downloadTabCols
                    : rest?.tabs && rest?.tabs?.length > 0
                      ? tabCols
                      : shownColumns;

                cols
                    ?.filter((c: any) => !!c?.isIncludeInDownloadFile)
                    ?.forEach((c: any, _index: number) => {
                        const label = i18n.format(c.downloadHeaderValue || c.label, data) || '';
                        let value: any = '—';
                        if (c.downloadDataValue || c.value) {
                            value =
                                i18n.format(c.downloadDataValue || c.value, {
                                    ...r,
                                    ...data,
                                }) || '—';
                        }

                        row[label] = value;
                        const width =
                            label?.length >= value?.length ? label?.length : value?.length;
                        wsCols[_index] = {
                            wch: width > 10 ? width + 5 : 10,
                        };
                    });
                // add rows to worksheet
                rowsData.push(row);
            });

            const ws: WorkSheet = utils?.json_to_sheet(rowsData);
            if (ws) {
                ws['!cols'] = wsCols;
                ws['!merges'] = wsMerges;
                ws['!rows'] = wsRows;

                // apply style to worksheet
                for (const i in ws as CellObject) {
                    if (typeof ws[i] === 'object') {
                        ws[i].s = {
                            alignment: {
                                horizontal: 'left', // align text to right
                            },
                        };
                    }
                }
            }

            // append each sheet to workbook
            utils?.book_append_sheet(
                wb,
                ws,
                `${i18n.format(rest?.downloadFileTabName || 'tab', data)}`
            );

            let excelBuffer: string = '';
            if (wb) {
                excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
            }

            // check file type for worksheet
            if (fileType === '.xlsx') {
                const _data = new Blob([excelBuffer], { type: fileType });
                saveAs(
                    _data,
                    `${i18n.format(
                        rest?.downloadFileName ||
                            (rest?.fileNameBasedOnTab &&
                                (rest.tabPreDescription || '') +
                                    i18n.format(selectedDownloadTabName || selectedTabName, data) +
                                    (rest?.tabDescription || '')) ||
                            'download',
                        data
                    )}.xlsx`
                );
            } else if (fileType === '.csv') {
                // read xlsx file
                import('xlsx').then(({ read }) => {
                    // use XLSX here
                    const rs = read(excelBuffer, { type: 'file' });
                    // generate csv file
                    const csvBuffer: string = write(rs, { bookType: 'csv', type: 'array' });
                    const blobCSV = new Blob([csvBuffer], { type: 'text/plain;charset=UTF-8' });
                    saveAs(
                        blobCSV,
                        `${i18n.format(
                            rest?.downloadFileName ||
                                (rest?.fileNameBasedOnTab &&
                                    (rest.tabPreDescription || '') +
                                        i18n.format(
                                            selectedDownloadTabName || selectedTabName,
                                            data
                                        ) +
                                        (rest?.tabDescription || '')) ||
                                'download',
                            data
                        )}.csv`
                    );
                });
            }
        });

        tracking({
            event: 'Download',
            data: data,
            dynamicEvents: dynamicEvents,
            useAnalytics: useAnalytics,
            useLowerCase: useLowerCase,
        });
    };

    const selectedTabInfo: Tab = rest?.tabs?.find((t: Tab) => t?.id === selectedTab);

    const applyOptionsFilters = (rows: any[], filters: any[]) => {
        return rows?.filter((rowData: any) => {
            let includeData = true;
            filters
                ?.filter((df: any) => df?.selectedOpts?.length > 0)
                ?.forEach((filter: any) => {
                    const { selectedOpts, filterBy } = filter;
                    const filterByParts = filterBy.split(',');

                    let matches;
                    if (filterByParts.length === 1) {
                        matches = selectedOpts
                            .map((opt: any) => opt?.toLowerCase())
                            .includes(rowData?.[filterBy]?.toLowerCase());
                    } else {
                        if (filtersUI.strictMatchingEnabled) {
                            matches = selectedOpts.every((opt: any) =>
                                filterByParts.some(
                                    (filterByItem: any) =>
                                        rowData?.[filterByItem]?.toLowerCase() === opt.toLowerCase()
                                )
                            );
                        } else {
                            matches = filterByParts.some((filterByItem: any) =>
                                selectedOpts
                                    .map((opt: any) => opt.toLowerCase())
                                    .includes(rowData?.[filterByItem]?.toLowerCase())
                            );
                        }
                    }

                    if (!matches) {
                        includeData = false;
                    }
                });
            return includeData;
        });
    };

    const { currentRowsData, groupsCount, tabFilteredData, rowsDataBeforePagination } = useMemo<{
        currentRowsData: any;
        groupsCount: Record<string, number>;
        tabFilteredData: any;
        rowsDataBeforePagination: any;
    }>(() => {
        const firstPageIndex = (currentPage - 1) * pageSize;
        const lastPageIndex = firstPageIndex + pageSize;
        let _rowsData: any = transformedTabData.rowsData;

        // Apply normal selection filters
        if (filterBySelection?.length) {
            filterBySelection.forEach((d) => {
                const list = selectionLists?.[d.storageName];
                _rowsData = _rowsData?.filter((data: any) => list?.includes(data[d.id]));
            });
        }

        // Apply watchlist filter
        if (filterByWatchList && watchlist?.length) {
            _rowsData = _rowsData?.filter((d: any) =>
                watchlist.includes(d[rest.watchlistDataId || 'id'])
            );
        }
        if (!watchlist.length && filterByWatchList) {
            setFilterByWatchlist(false);
        }

        // Apply text filters
        _rowsData = _rowsData?.filter((rowData: any) =>
            textFilters.length > 0
                ? textFilters.some((filter: any) =>
                      rowData?.[filter?.filterBy]
                          ?.toLowerCase()
                          ?.includes(filter?.value?.toLowerCase())
                  )
                : true
        );

        // Apply dropdown filters
        _rowsData = applyOptionsFilters(_rowsData, dropdownFilters);

        // Apply date filters
        if (dateFilter && dateFilter?.filterBy) {
            _rowsData = _rowsData?.filter((rowData: any) => {
                const isCustomDateRangePicker = filters.some(
                    (filter: any) => filter?.type === 'Custom Date range picker'
                );

                const dataDate = new Date(rowData?.[dateFilter.filterBy]);
                const minDate = dateFilter.minDate ? new Date(dateFilter.minDate) : null;
                const maxDate = dateFilter.maxDate ? new Date(dateFilter.maxDate) : null;

                // Different logic applied for custom date range picker
                if (isCustomDateRangePicker) {
                    const toUTCDateString = (date: string | null | undefined): Date | null => {
                        // Return null if no valid date
                        if (!date) return null;
                        const parts = date.split('-').map((part) => parseInt(part, 10));
                        // Ensure valid date format
                        if (parts.length !== 3 || parts.some(isNaN)) return null;
                        return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
                    };

                    const toUTCDate = (date: any): Date | null => {
                        if (!date) return null;
                        const d = new Date(date);
                        return isNaN(d.getTime())
                            ? null
                            : new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
                    };

                    const dataDate = toUTCDateString(rowData?.[dateFilter.filterBy]) ?? null;
                    const minDate = toUTCDate(dateFilter.minDate);
                    const maxDate = toUTCDate(dateFilter.maxDate);

                    return (
                        dataDate === null ||
                        ((!minDate || dataDate >= minDate) && (!maxDate || dataDate <= maxDate))
                    );
                }

                return (!minDate || dataDate >= minDate) && (!maxDate || dataDate <= maxDate);
            });
        }

        // Apply single date filter
        if (singleDateFilter && singleDateFilter?.filterBy) {
            _rowsData = _rowsData?.filter((rowData: any) => {
                const dataDate = new Date(rowData?.[singleDateFilter.filterBy]);
                const filterDate = new Date(singleDateFilter.date);

                // Compare dates by ensuring both are set to the same day
                return (
                    dataDate.getFullYear() === filterDate.getFullYear() &&
                    dataDate.getMonth() === filterDate.getMonth() &&
                    dataDate.getDate() + 1 === filterDate.getDate()
                );
            });
        }

        // Apply multi-select filters
        if (multiSelectFilters && multiSelectFilters?.length > 0) {
            _rowsData = applyOptionsFilters(_rowsData, multiSelectFilters);
        }

        setFilteredRowsData(_rowsData);
        // Store filtered data length
        setRowsDataLength(_rowsData.length);

        // Apply tab filters
        let _tabFilteredData = _rowsData;

        if (tabFilters && tabFilters[selectedTab]) {
            const {
                textFilters: _textFilters = [],
                dropdownFilters: _tabDropdownFilters = [],
                dateFilter: _dateFilter,
                singleDateFilter: _singleDateFilter,
                multiSelectFilters: _multiSelectFilters = [],
            } = tabFilters[selectedTab];

            // Apply text filters
            _tabFilteredData =
                (_tabFilteredData &&
                    _tabFilteredData?.length > 0 &&
                    _tabFilteredData?.filter((rowData: any) =>
                        _textFilters?.length > 0
                            ? _textFilters?.some((filter: any) =>
                                  rowData?.[filter?.filterBy]
                                      ?.toLowerCase()
                                      ?.includes(filter?.value?.toLowerCase())
                              )
                            : true
                    )) ||
                _tabFilteredData;

            // Apply dropdown filters
            _tabFilteredData =
                applyOptionsFilters(_tabFilteredData, _tabDropdownFilters) || _tabFilteredData;

            // Apply date _dateFilter
            if (_dateFilter && _dateFilter?.filterBy) {
                _tabFilteredData =
                    _tabFilteredData?.filter((rowData: any) => {
                        const dataDate = new Date(rowData?.[_dateFilter.filterBy]);
                        return (
                            (!_dateFilter.minDate || dataDate >= new Date(_dateFilter.minDate)) &&
                            (!_dateFilter.maxDate || dataDate <= new Date(_dateFilter.maxDate))
                        );
                    }) || _tabFilteredData;
            }

            // Apply single date filter
            if (_singleDateFilter && _singleDateFilter?.filterBy) {
                _tabFilteredData = _tabFilteredData?.filter((rowData: any) => {
                    const dataDate = new Date(rowData?.[_singleDateFilter.filterBy]);
                    const filterDate = new Date(_singleDateFilter.date);

                    // Compare dates by ensuring both are set to the same day
                    return (
                        dataDate.getFullYear() === filterDate.getFullYear() &&
                        dataDate.getMonth() === filterDate.getMonth() &&
                        dataDate.getDate() + 1 === filterDate.getDate()
                    );
                });
            }

            // Apply multi-select filters
            if (_multiSelectFilters && _multiSelectFilters?.length > 0) {
                _tabFilteredData =
                    applyOptionsFilters(_tabFilteredData, _multiSelectFilters) || _tabFilteredData;
            }

            setFilteredRowsData(_tabFilteredData);
            setRowsDataLength(_tabFilteredData?.length);
        }

        let groupsCount = {};
        // Group data
        if (enableGrouping && groupingPropertyId) {
            const tmpData = getGroupedData(_rowsData, groupingPropertyId);
            _rowsData = tmpData.data;
            groupsCount = tmpData.groupsCount;
        }

        // setting it back to start, shows updated result with new filtered data
        if (!enableGrouping) {
            _rowsData = (_rowsData || []).sort(sortItemsWithoutGrouping(sortId, sorting));
            _tabFilteredData = (_tabFilteredData || []).sort(
                sortItemsWithoutGrouping(sortId, sorting)
            );
            setFilteredData(selectedTabInfo?.hasTabFilter ? _tabFilteredData : _rowsData);
        } else {
            // Sorting logic
            const sortData = (sData: any) => {
                const newArray = [];
                for (const key in sData) {
                    // eslint-disable-next-line no-prototype-builtins
                    if (sData.hasOwnProperty(key)) {
                        const arrayForKey = sData[key];
                        for (let i = 0; i < arrayForKey.length; i++) {
                            const sortedList = (arrayForKey || []).sort((a: any, b: any) => {
                                const valueA = a[sortId];
                                const valueB = b[sortId];

                                // Convert "—" and undefined to 0 for numeric comparison
                                const numericValueA =
                                    valueA === '—' || valueA === null || valueA === undefined
                                        ? 0
                                        : valueA;
                                const numericValueB =
                                    valueB === '—' || valueB === null || valueB === undefined
                                        ? 0
                                        : valueB;

                                if (
                                    typeof numericValueA === 'number' &&
                                    typeof numericValueB === 'number'
                                ) {
                                    // Compare numeric values
                                    return sorting === 'ascending'
                                        ? numericValueA - numericValueB
                                        : numericValueB - numericValueA;
                                } else if (typeof numericValueA === 'number' && valueB === '—') {
                                    // Place "—" values at the end
                                    return sorting === 'ascending' ? -1 : 1;
                                } else if (valueA === '—' && typeof numericValueB === 'number') {
                                    // Place "—" values at the end
                                    return sorting === 'ascending' ? 1 : -1;
                                } else {
                                    // Compare string values
                                    return sorting === 'ascending'
                                        ? String(valueA || '—').localeCompare(
                                              String(valueB || '—'),
                                              undefined,
                                              {
                                                  sensitivity: 'base',
                                              }
                                          )
                                        : String(valueB || '—').localeCompare(
                                              String(valueA || '—'),
                                              undefined,
                                              {
                                                  sensitivity: 'base',
                                              }
                                          );
                                }
                            });
                            newArray.push(sortedList[i]);
                        }
                    }
                }
                return newArray;
            };

            _rowsData = sortData(_rowsData);
            _tabFilteredData = sortData(_tabFilteredData);
            setFilteredData(selectedTabInfo?.hasTabFilter ? _tabFilteredData : _rowsData);
        }

        const rowsDataBeforePagination = _rowsData;

        // Pagination logic
        if (enablePagination) {
            if (
                (_rowsData && (_rowsData?.length || Object.keys(_rowsData)?.length)) ||
                (tabFilters && tabFilters?.[selectedTab] && _tabFilteredData?.length)
            ) {
                const tmp =
                    enableGrouping && groupingPropertyId
                        ? Object.values(_rowsData).flat().slice(firstPageIndex, lastPageIndex)
                        : _rowsData.slice(firstPageIndex, lastPageIndex);

                const tabTmp =
                    enableGrouping && groupingPropertyId
                        ? Object.values(_tabFilteredData)
                              .flat()
                              .slice(firstPageIndex, lastPageIndex)
                        : _tabFilteredData.slice(firstPageIndex, lastPageIndex);

                return enableGrouping && groupingPropertyId
                    ? {
                          currentRowsData: getGroupedData(tmp, groupingPropertyId)?.data,
                          groupsCount,
                          tabFilteredData: getGroupedData(tabTmp, groupingPropertyId)?.data,
                          rowsDataBeforePagination,
                      }
                    : {
                          currentRowsData: tmp,
                          groupsCount,
                          tabFilteredData: tabTmp,
                          rowsDataBeforePagination,
                      };
            }
        }

        return {
            currentRowsData: _rowsData,
            groupsCount,
            tabFilteredData: _tabFilteredData,
            rowsDataBeforePagination,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentPage,
        pageSize,
        filterByWatchList,
        watchlist,
        enableGrouping,
        groupingPropertyId,
        enablePagination,
        rest.watchlistDataId,
        filterBySelection,
        textFilters,
        dropdownFilters,
        dateFilter,
        tabFilters,
        selectedTab,
        filtersUI,
        sorting,
        sortId,
        singleDateFilter,
        transformedTabData.rowsData,
        multiSelectFilters,
    ]);

    useEffect(() => {
        setCurrentPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sorting, sortId, textFilters, dropdownFilters, dateFilter, filterByWatchList, tabFilters]);

    const onChangeSingleDate = (
        date: any,
        filterBy?: string,
        _analyticsLabel?: string,
        _dateFilterByKey?: string,
        isTabFilter?: boolean
    ) => {
        if (isTabFilter && filterBy) {
            // Update tab-specific single date filter
            setTabFilters({
                ...tabFilters,
                [selectedTab]: {
                    ...tabFilters?.[selectedTab],
                    singleDateFilter: { date, filterBy },
                },
            });
        } else if (!isTabFilter && filterBy) {
            // Update global single date filter
            setSingleDateFilter({ date, filterBy });
        }

        tracking({
            event: 'Single Date Filter',
            data: data,
            dynamicEvents: dynamicEvents,
            useAnalytics: useAnalytics,
            filterAnalyticLabel: _analyticsLabel,
            useLowerCase: useLowerCase,
        });
    };

    const renderControls = () => {
        return (
            <div className={'table-controls-container'}>
                <div
                    className={`show-all-funds-button ${
                        !filterByWatchList && filterBySelection.length === 0 ? 'disabled' : ''
                    }`}
                    onClick={() => {
                        setFilterByWatchlist(false);
                        setFilterBySelection([]);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            setFilterByWatchlist(false);
                            setFilterBySelection([]);
                        }
                    }}
                    tabIndex={0}
                >
                    <div className="arrow left" />
                    <span>{i18n.format(rest.showAllFundsBtnText, _data)}</span>
                </div>
                <div className={'left-component'}>
                    {selectedTabInfo && selectedTabInfo.headerText && (
                        <span
                            dangerouslySetInnerHTML={{
                                __html: i18n.format(selectedTabInfo.headerText, _data),
                            }}
                        />
                    )}

                    {rest.isAddPlaceholderInLeftPanelOfTableCtrls && (
                        <Placeholder
                            contentPlaceHolderId={`${CHILDREN_PLACEHOLDER_ID}-leftPanel`}
                            parentWidgetInstanceId={widgetInstance?.id}
                            data={{
                                ...data,
                                downloadFileColumns: (rest?.tabs && rest?.tabs?.length > 0
                                    ? tabCols
                                    : shownColumns
                                )
                                    ?.filter(
                                        (c: any) => c?.isIncludeInDownloadFile && c.label && c.value
                                    )
                                    ?.map((c) => ({
                                        label: c.downloadHeaderValue || c.label,
                                        value: c.downloadDataValue || c.value,
                                    })) as { label: string; value: string }[],
                            }}
                        />
                    )}
                </div>
                <div className={'right-component'}>
                    {rest.isAddPlaceholderInRightPanelOfTableCtrls && (
                        <Placeholder
                            contentPlaceHolderId={`${CHILDREN_PLACEHOLDER_ID}-rightPanel`}
                            parentWidgetInstanceId={widgetInstance?.id}
                            data={{
                                ...data,
                                downloadFileColumns: (rest?.tabs && rest?.tabs?.length > 0
                                    ? tabCols
                                    : shownColumns
                                )
                                    ?.filter(
                                        (c: any) => c?.isIncludeInDownloadFile && c.label && c.value
                                    )
                                    ?.map((c) => ({
                                        label: c.downloadHeaderValue || c.label,
                                        value: c.downloadDataValue || c.value,
                                    })) as { label: string; value: string }[],
                            }}
                        />
                    )}
                    {rest.enableWatchlist ? (
                        <button
                            className={`watchlist-button ${
                                filterByWatchList && watchlist.length ? 'selected' : ''
                            }`}
                            disabled={!watchlist.length}
                            onClick={() => {
                                setFilterByWatchlist(!filterByWatchList);
                            }}
                        >
                            <span
                                className="watchlist-button-text"
                                dangerouslySetInnerHTML={{
                                    __html:
                                        i18n.format(rest.watchlistButtonText, _data) ||
                                        i18n.t('watchlist'),
                                }}
                            ></span>
                            {watchlist.length ? (
                                <span className="watchlist-count">{watchlist.length}</span>
                            ) : (
                                ''
                            )}
                        </button>
                    ) : (
                        ''
                    )}
                    {rest?.enableDownload && !rest?.showDownloadAtBottom ? (
                        <button
                            className={`download-button`}
                            onClick={() => onDownloadClick()}
                            dangerouslySetInnerHTML={{
                                __html:
                                    i18n.format(rest.downloadButtonText, _data) ||
                                    i18n.t('download'),
                            }}
                        />
                    ) : (
                        ''
                    )}
                    {rest?.downloadBooklet ? (
                        <DownloadBooklet
                            data={data}
                            storageName={
                                columns?.find((c: any) => c.selectionUsedFor === 'downloadBooklet')
                                    ?.selectionListStorageName
                            }
                            selectionList={
                                selectionLists[
                                    columns?.find(
                                        (c: any) => c.selectionUsedFor === 'downloadBooklet'
                                    )?.selectionListStorageName || ''
                                ]
                            }
                            downloadBooklet={rest?.downloadBooklet}
                            downloadBookletButtonText={rest?.downloadBookletButtonText}
                            showDownloadBookletIcon={rest?.showDownloadBookletIcon}
                            downloadBookletIconType={rest?.downloadBookletIconType}
                            downloadBookletIconPosition={rest?.downloadBookletIconPosition}
                            downloadBookletIconName={rest?.downloadBookletIconName}
                            downloadBookletImage={rest?.downloadBookletImage}
                            downloadBookletMessage={rest?.downloadBookletMessage}
                            downloadBookletMessageIcon={rest?.downloadBookletMessageIcon}
                            downloadBookletMessageIconName={rest?.downloadBookletMessageIconName}
                            downloadBookletMessageIconType={rest?.downloadBookletMessageIconType}
                            downloadBookletMessageIconPosition={
                                rest?.downloadBookletMessageIconPosition
                            }
                            downloadBookletMessageImage={rest?.downloadBookletMessageImage}
                            downloadBookletErrorMessage={rest?.downloadBookletErrorMessage}
                            downloadBookletErrorMessageIcon={rest?.downloadBookletErrorMessageIcon}
                            downloadBookletErrorMessageIconType={
                                rest?.downloadBookletErrorMessageIconType
                            }
                            downloadBookletErrorMessageIconPosition={
                                rest?.downloadBookletErrorMessageIconPosition
                            }
                            downloadBookletErrorMessageIconName={
                                rest?.downloadBookletErrorMessageIconName
                            }
                            downloadBookletErrorMessageImage={
                                rest?.downloadBookletErrorMessageImage
                            }
                            downloadLinkText={rest?.downloadLinkText}
                            downloadBookletText={rest?.downloadBookletText}
                            bookletName={rest?.bookletName}
                        />
                    ) : null}
                    {shownColumns
                        ?.filter(
                            (item: any) =>
                                item.selectionUsedFor === 'compare' && item.selectionListStorageName
                        )
                        .map((item: any) => (
                            <button
                                className={`compare-btn ${
                                    filterBySelection?.findIndex(
                                        (d) => d.storageName === item.selectionListStorageName
                                    )
                                        ? ''
                                        : 'selected'
                                }`}
                                disabled={
                                    !selectionLists[item.selectionListStorageName]?.length ||
                                    selectionLists[item.selectionListStorageName]?.length <
                                        item.minSelectionCount
                                }
                                key={item.id}
                                onClick={() => {
                                    selectionLists[item.selectionListStorageName]?.length &&
                                        onClickCompare(
                                            item.selectionListStorageName,
                                            item.selectionListId,
                                            item.id,
                                            item.changeToTab
                                        );
                                }}
                            >
                                <span className="compare-btn-text">
                                    {filterBySelection?.findIndex(
                                        (d) => d.storageName === item.selectionListStorageName
                                    )
                                        ? i18n.format(item.label, data)
                                        : i18n.format(item.ActiveButtonText, data)}
                                </span>
                                {item.labelDescription && (
                                    <div className="compare-btn-description">
                                        {i18n.format(item.labelDescription, data)}
                                    </div>
                                )}
                            </button>
                        ))}
                </div>
            </div>
        );
    };

    return (
        <div
            css={css`
                ${!theme ? '' : customizedFundListStyles}${!theme
                    ? ''
                    : baseComponents.filters.filtersStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles || ''}
            `}
            className={widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''}
        >
            <div className="sr-only" aria-live="assertive">
                {
                    i18n.translate('widgets_fundList_dataUpdate', {
                        count: filteredData.length,
                    }) as any
                }
            </div>
            <baseComponents.filters.Filters
                isInCustomizedFundList={true}
                data={_data}
                filters={filters}
                filtersUI={filtersUI}
                filteredData={filteredRowsData}
                selectedFilters={{ dropdownFilters, multiSelectFilters }}
                onChangeTextFilter={(value: string, filterBy?: string, analyticsLabel?: string) =>
                    onChangeTextFilter(value, filterBy, analyticsLabel)
                }
                onSelectOption={onChangeDropdownOpt}
                onClearAllFilters={() => {
                    setTextFilters([]);
                    setDropdownFilters(null);
                    setMultiSelectFilters([]);
                }}
                onCancelFilter={(filter: any, filterType: 'dropdown' | 'multiSelect') =>
                    onCancelFilter(filter, filterType)
                }
                onChangeDateRange={(
                    minDate: any,
                    maxDate: any,
                    filterBy?: string,
                    analyticsLabel?: string,
                    startDateFilterByKey?: string,
                    endDateFilterByKey?: string
                ) =>
                    onChangeDateRange(
                        minDate,
                        maxDate,
                        filterBy,
                        analyticsLabel,
                        startDateFilterByKey,
                        endDateFilterByKey
                    )
                }
                staticDataTabIndex={staticDataTabIndex}
                onChangeSingleDate={onChangeSingleDate}
                onChangeMultiSelect={onChangeMultiSelect}
                onClickTextFilter={(analyticsLabel?: string) => onClickTextFilter(analyticsLabel)}
            />
            {!rest.addPlaceholderWhenNoData ||
            currentRowsData?.length ||
            (enableGrouping &&
                groupingPropertyId &&
                currentRowsData &&
                Object.keys(currentRowsData)?.length) ? (
                <>
                    {rest?.moveControlsToTop && renderControls()}
                    <Tabs
                        data={data}
                        tabs={rest?.tabs}
                        hideTabs={rest?.hideTabs}
                        isSticky={rest?.isTabsSticky}
                        selected={selectedTab}
                        onChange={(id) => {
                            setSelectedTab(id);
                            setFilterBySelection([]);
                            tracking({
                                event: 'Tab Selection',
                                data: data,
                                dynamicEvents: dynamicEvents,
                                useAnalytics: useAnalytics,
                                selectedTabValue: id,
                                useLowerCase: useLowerCase,
                            });
                        }}
                    />
                    {!rest?.moveControlsToTop && renderControls()}
                    {rest.enableColumnToggling && (
                        <baseComponents.multiSelectModal.MultiSelectModal
                            hideToggle={selectedTabInfo?.hideColumnVisibilityToggle}
                            selectableItems={
                                columns?.map((c: any) => ({
                                    ...c,
                                    label: i18n.format(c.label || '', data),
                                })) || []
                            }
                            itemIdsToShow={columns
                                ?.filter((col: ColumnOption) =>
                                    selectedTabInfo?.tabColumns?.split(',')?.includes(col?.id || '')
                                )
                                .map((c: any) => c.id || '')}
                            modalTitle={i18n.format(
                                rest?.columnTogglingTitle || 'Show / Hide Columns',
                                data
                            )}
                            modalSubtitle={i18n.format(
                                rest?.columnTogglingSubtitle ||
                                    'Select the columns you want to display',
                                data
                            )}
                            selectAllText={i18n.format(
                                rest?.columnTogglingSelectAllText || 'Select All',
                                data
                            )}
                            deselectAllText={i18n.format(
                                rest?.columnTogglingDeselectAllText || 'Deselect All',
                                data
                            )}
                            saveText={i18n.format(rest?.columnTogglingSaveText || 'Save', data)}
                            cancelText={i18n.format(
                                rest?.columnTogglingCancelText || 'Cancel',
                                data
                            )}
                            selectedItems={shownColumns}
                            setSelectedItems={setShownColumns}
                        />
                    )}
                    {selectedTabInfo?.hasTabFilter && (
                        <baseComponents.filters.Filters
                            isInCustomizedFundList={true}
                            data={_data}
                            filters={selectedTabInfo?.filters}
                            filtersUI={selectedTabInfo?.filtersUI}
                            filteredData={filteredRowsData}
                            selectedFilters={{ dropdownFilters, multiSelectFilters }}
                            defaultTextValue={tabFilters?.[selectedTab]?.textFilters?.[0]?.value}
                            onChangeTextFilter={(
                                value: string,
                                filterBy?: string,
                                analyticsLabel?: string
                            ) => onChangeTextFilter(value, filterBy, analyticsLabel, true)}
                            onSelectOption={(
                                selectedOpts: string[],
                                filterBy?: string,
                                label?: string,
                                defaultValue?: string,
                                analyticsLabel?: string,
                                _filterByKey?: string,
                                selectAllLabel?: string,
                                selectCurrentOption?: string
                            ) =>
                                onChangeDropdownOpt(
                                    selectedOpts,
                                    filterBy,
                                    label,
                                    defaultValue,
                                    analyticsLabel,
                                    _filterByKey,
                                    selectAllLabel,
                                    selectCurrentOption || '',
                                    true
                                )
                            }
                            onClearAllFilters={() => {
                                setTabFilters(null);
                            }}
                            onCancelFilter={(filter: any, filterType: 'dropdown' | 'multiSelect') =>
                                onCancelFilter(filter, filterType, true)
                            }
                            onChangeDateRange={(
                                minDate: any,
                                maxDate: any,
                                filterBy?: string,
                                analyticsLabel?: string,
                                startDateFilterByKey?: string,
                                endDateFilterByKey?: string
                            ) =>
                                onChangeDateRange(
                                    minDate,
                                    maxDate,
                                    filterBy,
                                    analyticsLabel,
                                    startDateFilterByKey,
                                    endDateFilterByKey,
                                    true
                                )
                            }
                            staticDataTabIndex={staticDataTabIndex}
                            onChangeSingleDate={(
                                date: any,
                                filterBy?: string,
                                _analyticsLabel?: string,
                                _dateFilterByKey?: string
                            ) =>
                                onChangeSingleDate(
                                    date,
                                    filterBy,
                                    _analyticsLabel,
                                    _dateFilterByKey,
                                    true
                                )
                            }
                            onClickTextFilter={(analyticsLabel?: string) =>
                                onClickTextFilter(analyticsLabel)
                            }
                            onChangeMultiSelect={(selectedOpts: string[], filterBy?: string) =>
                                onChangeMultiSelect(selectedOpts, filterBy, true)
                            }
                        />
                    )}
                    {(
                        selectedTabInfo?.hasTabFilter
                            ? tabFilteredData &&
                              (tabFilteredData?.length || Object.keys(tabFilteredData)?.length)
                            : currentRowsData &&
                              (currentRowsData?.length || Object.keys(currentRowsData)?.length)
                    ) ? (
                        <Table
                            data={data}
                            columns={rest?.tabs && rest?.tabs?.length > 0 ? tabCols : shownColumns}
                            rows={selectedTabInfo?.hasTabFilter ? tabFilteredData : currentRowsData}
                            rowsDataBeforePagination={rowsDataBeforePagination}
                            columnGroups={rest.columnGroups}
                            groupsCount={groupsCount}
                            enableGrouping={enableGrouping}
                            showGroupingCount={rest.showGroupingCount}
                            isCollapseRowGroup={rest.isCollapseRowGroup}
                            showRowGroupIconName={rest.showRowGroupIconName}
                            hideRowGroupIconName={rest.hideRowGroupIconName}
                            groupingPropertyId={groupingPropertyId}
                            widgetInstance={widgetInstance}
                            onChangeSorting={onChangeSorting}
                            enableWatchlist={rest.enableWatchlist}
                            watchlist={watchlist}
                            setWatchlist={setWatchlist}
                            watchlistStorageName={rest.watchlistStorageName}
                            watchlistDataId={rest.watchlistDataId}
                            addToWatchlistText={
                                i18n.format(rest.addToWatchlistText || '', _data) ||
                                i18n.t('add_to_watchlist')
                            }
                            removeFromWatchlistText={
                                i18n.format(rest.removeFromWatchlistText || '', _data) ||
                                i18n.t('remove_from_watchlist')
                            }
                            mobileBreakpoint={rest.mobileBreakpoint}
                            onClickCompare={(
                                storageName: string,
                                id: string,
                                colId: string,
                                changeToTab?: string
                            ) => onClickCompare(storageName, id, colId, changeToTab)}
                            onChangeSelectionLists={(data: Record<string, string[]>) =>
                                setSelecionLists(data)
                            }
                            filterBySelection={filterBySelection}
                            selectionLists={selectionLists || {}}
                            selectedTab={selectedTab || ''}
                            staticDataTabIndex={staticDataTabIndex}
                            sorting={sorting}
                            defaultSortingOrder={rest.defaultSortingOrder}
                            onTriggerTracking={onTriggerTracking}
                            useAnalytics={useAnalytics}
                            downloadBooklet={rest.downloadBooklet}
                        />
                    ) : (
                        <div className={'no-data-container'}>
                            <Placeholder
                                contentPlaceHolderId={`${CHILDREN_PLACEHOLDER_ID}-nodataplaceholder`}
                                parentWidgetInstanceId={widgetInstance?.id}
                                data={data}
                            />
                        </div>
                    )}
                </>
            ) : (
                <div className={'no-data-container'}>
                    <Placeholder
                        contentPlaceHolderId={`${CHILDREN_PLACEHOLDER_ID}-nodataplaceholder`}
                        parentWidgetInstanceId={widgetInstance?.id}
                        data={data}
                    />
                </div>
            )}
            {enablePagination && rowsDataLength ? (
                <Pagination
                    currentPage={currentPage}
                    totalCount={rowsDataLength}
                    pageSize={pageSize}
                    onPageChange={(page: any) => setCurrentPage(page)}
                    showPageSizeDropDown={showPageSizePicker}
                    pazeSizeOptions={(rest.configurePageSizes || '10,20,50,100')
                        .split(',')
                        .map((d: string) => parseInt(d))}
                    onChangePageSize={(e) => {
                        if (Math.ceil(rowsDataLength / e) < currentPage) {
                            setCurrentPage(1);
                        }
                        return setPageSize(parseInt(e));
                    }}
                    showCurrentPageText={rest.showCurrentPageText}
                    pagingType={rest.pagingType || 'simple_numbers'}
                    enableShowAll={rest.enableShowAll}
                    showAllText={i18n.format(rest.showAllText || 'All', _data)}
                    pagePickerPosition={rest.pagePickerPosition || 'right'}
                    pageSizePickerPosition={rest.pageSizePickerPosition || 'left'}
                    currentPageTextPosition={rest.currentPageTextPosition || 'left'}
                    pagerButtonStyle={rest.pagerButtonStyle || 'circle'}
                    previousBtnText={i18n.format(rest.previousBtnText || '', _data)}
                    nextBtnText={i18n.format(rest.nextBtnText || '', _data)}
                    lastBtnText={i18n.format(rest.lastBtnText || '', _data)}
                    firstBtnText={i18n.format(rest.firstBtnText || '', _data)}
                    pagePickerDropdownImage={rest.pagePickerDropdownImage || ''}
                    currentPageText={
                        i18n.format(rest.currentPageText || '', {
                            ..._data,
                            pageTotalCount: rowsDataLength,
                            pageFrom,
                            pageTo,
                        }) || `${pageFrom} - ${pageTo} of ${rowsDataLength}`
                    }
                    pageSizePickerText={i18n.format(rest.pageSizePickerText || '', _data)}
                    staticDataTabIndex={staticDataTabIndex}
                    showAllRows={showAllRows}
                    setShowAllRows={setShowAllRows}
                />
            ) : (
                ''
            )}
            {selectedTabInfo?.footerText && (
                <div className="tab-footer-text-container">
                    <p
                        className="tab-footer-text"
                        dangerouslySetInnerHTML={{
                            __html: i18n.format(selectedTabInfo.footerText, _data),
                        }}
                    />
                </div>
            )}
            {rest?.isAddPlaceholderInBottomPanelOfTableCtrls ||
            (rest?.enableDownload && rest?.showDownloadAtBottom) ? (
                <div className={'bottom-component'}>
                    {rest.enableDownload && rest?.showDownloadAtBottom ? (
                        <button
                            className={`download-button`}
                            onClick={() => onDownloadClick()}
                            dangerouslySetInnerHTML={{
                                __html:
                                    i18n.format(rest.downloadButtonText, _data) ||
                                    i18n.t('download'),
                            }}
                        />
                    ) : (
                        ''
                    )}
                    {rest?.isAddPlaceholderInBottomPanelOfTableCtrls ? (
                        <Placeholder
                            contentPlaceHolderId={`${CHILDREN_PLACEHOLDER_ID}-bottomPanel`}
                            parentWidgetInstanceId={widgetInstance?.id}
                            data={{
                                ...data,
                                downloadFileColumns: (rest?.tabs && rest?.tabs?.length > 0
                                    ? tabCols
                                    : shownColumns
                                )
                                    ?.filter(
                                        (c: any) => c?.isIncludeInDownloadFile && c.label && c.value
                                    )
                                    ?.map((c) => ({
                                        label: c.downloadHeaderValue || c.label,
                                        value: c.downloadDataValue || c.value,
                                    })) as { label: string; value: string }[],
                            }}
                        />
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default CustomizedFundList;
